import Feedback from '@/components/Feedback/Feedback';
import AchievementIcon from '@/components/Icon/AchievementIcon';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import useAchievements from '@/hooks/useAchievements';
import cn from '@/lib/cs';
import { useMemo } from 'react';
import AchievementListItem from './AchievementListItem';

export type AchievementsOverviewProps = Pick<
	ReturnType<typeof useAchievements>,
	'achievements' | 'score'
> & {
	className?: string;
	headingElement?: React.ElementType;
	isInsideDialog: boolean;
};

export const AchievementsOverview = ({
	achievements,
	score,
	className,
	headingElement,
	isInsideDialog,
}: AchievementsOverviewProps) => {
	const onlyScoredAchievements = useMemo(
		() =>
			achievements.filter(
				(achievement) => achievement.calculatedTotalScore > 0,
			),
		[achievements],
	);

	const missingAchievements = useMemo(
		() =>
			achievements.filter(
				(achievement) => achievement.calculatedTotalScore === 0,
			),
		[achievements],
	);

	const Heading: React.ElementType = headingElement ?? 'h2';

	return (
		<div className={className}>
			<div
				className={cn(
					'relative mx-auto mb-4 max-w-xs text-white',
					isInsideDialog ? 'mt-28' : 'mt-8',
				)}>
				<LargeSquareBackground className="text-primary" />
				<div className="relative flex items-center justify-center gap-2 p-6">
					<AchievementIcon className="size-24 shrink-0 -translate-y-2 lg:size-24" />
					<Heading className="font-brand text-4xl">
						Challenges
						<span className="block text-3xl leading-8">
							<span className="sr-only">:</span>
							<span className="text-5xl">{score}</span> appels
						</span>
					</Heading>
				</div>
			</div>

			<ul className="grid gap-4 pb-12">
				{onlyScoredAchievements.map((achievement, index) => (
					<AchievementListItem
						key={achievement.id}
						achievement={achievement}
						index={index}
						headingElement="h3"
						isInsideDialog={isInsideDialog}
					/>
				))}
			</ul>

			{missingAchievements.length > 0 && (
				<p className="relative z-10 mb-12 px-8 py-4 text-center text-lg text-white">
					<LabelBackground className="scale-150 text-primary" />
					<span className="relative">
						Je hebt {missingAchievements.length}{' '}
						{missingAchievements.length === 1
							? 'challenge'
							: 'challenges'}{' '}
						nog niet ontdekt.{' '}
						{missingAchievements
							// Only hint at achievements that are visible when not achieved
							.filter(
								(missingAchievement) =>
									missingAchievement.isVisibleWhenNotAchieved,
							)
							// Limit to 4 hints
							.slice(0, 4)
							// Render hints
							.map(
								(missingAchievement, index, array) =>
									`${
										index === 0 ? 'Hint: ' : ''
									}${missingAchievement.title}${
										index < array.length - 1 ? ', ' : '.'
									}`,
							)}
					</span>
				</p>
			)}
			<Feedback />
		</div>
	);
};

export default AchievementsOverview;
